/*
 * File: /src/util/assetUtils.js
 * Created Date: Tuesday December 17th 2024
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Tuesday, 17th December 2024 9:09:19 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2024 AdBase, Inc.
 */

import { authedAxios } from 'src/shared/axiosWrapper.js';
import { AD_BASE_ASSETS_PATH } from 'src/constants.js';

export const ACCEPTED_ASSET_TYPES = {
  image: {
    mimeType: 'image/*',
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  },
  video: {
    mimeType: 'video/*',
    extensions: ['mp4', 'webm', 'ogg'],
  },
  audio: {
    mimeType: 'audio/*',
    extensions: ['mp3', 'wav', 'ogg'],
  },
  pdf: {
    mimeType: 'application/pdf',
    extensions: ['pdf'],
  },
  text: {
    mimeType: 'text/plain,text/rtf',
    extensions: ['txt', 'rtf'],
  },
  html: {
    mimeType: 'text/html',
    extensions: ['html', 'htm'],
  },
  binary: {
    mimeType: '*/*',
    extensions: ['*'],
  },
};

// SendSquared Asset Type.
export function detectAssetType(file) {
  const mimePrefix = file.type.split('/')[0];
  const extension = file.name.split('.').pop().toLowerCase();

  // Check mime type first
  if (mimePrefix === 'image') return 'image';
  if (mimePrefix === 'video') return 'video';
  if (mimePrefix === 'audio') return 'audio';

  // Then check extensions
  if (extension === 'pdf' || file.type === 'application/pdf') return 'pdf';
  if (['txt', 'rtf'].includes(extension) || mimePrefix === 'text')
    return 'text';
  if (['html', 'htm'].includes(extension) || file.type === 'text/html')
    return 'html';

  return 'binary';
}

export async function uploadAsset(file) {
  try {
    // Get signed URL for upload
    const endpoint = file.isVerification
      ? 'upload-verification-url'
      : 'uploadUrl';
    console.log(file);
    const result = await authedAxios.post(`${AD_BASE_ASSETS_PATH}${endpoint}`, {
      name: file.name,
      is_public: file.isPublic,
      mime_type: file.type,
      size: file.size,
      asset_type: detectAssetType(file),
    });

    // Upload to S3
    await fetch(result.data.url, {
      method: 'PUT',
      cache: 'no-cache',
      headers: {
        'Access-Control-Allow-Origin': '*',
      },
      redirect: 'follow',
      referrer: 'no-referrer',
      body: file,
    });

    // Confirm upload
    const confirmResponse = await authedAxios.put(
      `${AD_BASE_ASSETS_PATH}${result.data.id}/uploadComplete`
    );

    return {
      success: true,
      asset: confirmResponse.data,
    };
  } catch (error) {
    console.error('Error uploading asset:', error);
    return {
      success: false,
      error,
    };
  }
}

export function getAcceptedFileTypes(assetTypes) {
  return assetTypes
    .map((type) => ACCEPTED_ASSET_TYPES[type]?.mimeType || `${type}/*`)
    .join(',');
}

export async function validateAssetType(file, allowedTypes) {
  const assetType = detectAssetType(file);
  return allowedTypes.includes(assetType);
}

// Helper method to save media to assets from a url
export async function saveToAssets(mediaUrl, verification) {
  try {
    if (!mediaUrl || typeof mediaUrl !== 'string') {
      throw new Error('Invalid media URL provided');
    }

    const response = await fetch(mediaUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Create a default asset name outside of the hash or uuid
    const filename =
      mediaUrl.split('/').pop().split('?')[0] ||
      `mms-asset-${Date.now()}.${blob.type.split('/')[1]}`;

    const file = new File([blob], filename, { type: blob.type });

    file.isPublic = false;
    file.isVerification = verification;

    const assetType = detectAssetType(file);
    const allowedTypes = ['image', 'pdf', 'video'];

    // We could allow more types in the future
    if (!allowedTypes.includes(assetType)) {
      throw new Error(
        `Invalid file type. Only images, PDFs, and videos are supported.`
      );
    }

    const { success, asset, error } = await uploadAsset(file);

    if (!success) {
      throw new Error(error || 'Failed to upload asset');
    }

    return {
      success: true,
      asset,
    };
  } catch (error) {
    console.error('Error saving MMS to assets:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}
